// src/components/Admin/RecruiterManagement.jsx
import React, { useState } from 'react';
import { updateRecruiterStatus, assignTokens } from '../../services/recruiter';

const RecruiterManagement = ({ recruiters, onRefresh }) => {
    const [activeTab, setActiveTab] = useState('pending');
    const [loading, setLoading] = useState({});
    const [tokenModal, setTokenModal] = useState({ isOpen: false, recruiterId: null, currentTokens: 0 });
    const [tokenAmount, setTokenAmount] = useState(20);
    const [tokenLoading, setTokenLoading] = useState(false);
    const [tokenError, setTokenError] = useState('');

    const tabs = ['pending', 'approved', 'rejected'];
    const filteredRecruiters = recruiters.filter(r => r.status === activeTab);

    const handleStatusUpdate = async (uid, status) => {
        if (!window.confirm(`Are you sure you want to ${status} this recruiter?`)) return;
        setLoading(prev => ({ ...prev, [uid]: true }));
        const result = await updateRecruiterStatus(uid, status);
        if (result.success) {
            onRefresh();
        } else {
            alert('Error updating status: ' + result.error);
        }
        setLoading(prev => ({ ...prev, [uid]: false }));
    };

    const handleOpenTokenModal = (recruiter) => {
        setTokenModal({
            isOpen: true,
            recruiterId: recruiter.uid,
            currentTokens: recruiter.tokens || 0,
            recruiterName: `${recruiter.firstName || ''} ${recruiter.lastName || ''}`,
            companyName: recruiter.companyName || recruiter.businessType || 'Recruiter'
        });
        setTokenAmount(20);
        setTokenError('');
    };

    const handleCloseTokenModal = () => {
        setTokenModal({ isOpen: false, recruiterId: null, currentTokens: 0 });
        setTokenAmount(20);
        setTokenError('');
    };

    const handleAssignTokens = async () => {
        if (tokenAmount <= 0) {
            setTokenError('Please enter a valid amount');
            return;
        }

        setTokenLoading(true);
        setTokenError('');

        const result = await assignTokens(tokenModal.recruiterId, tokenAmount);
        if (result.success) {
            alert(`Successfully added ${tokenAmount} tokens to ${tokenModal.recruiterName}`);
            handleCloseTokenModal();
            onRefresh();
        } else {
            setTokenError('Failed to assign tokens: ' + result.error);
        }
        setTokenLoading(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-ZA', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    const getBusinessTypeLabel = (type) => {
        const labels = {
            'company': 'Company',
            'agency': 'Recruitment Agency',
            'sole': 'Sole Proprietor'
        };
        return labels[type] || type;
    };

    const presetAmounts = [10, 20, 50, 100, 200];

    return (
        <div>
            <h2 className="text-2xl font-bold text-gradpath-dark mb-6 flex items-center gap-2">
                <i className="fas fa-building text-gradpath-green"></i>
                Recruiters / Employers
            </h2>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200 mb-6">
                {tabs.map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 font-medium transition-all capitalize ${
                            activeTab === tab
                                ? 'text-gradpath-green border-b-2 border-gradpath-green'
                                : 'text-gray-600 hover:text-gradpath-green'
                        }`}
                    >
                        {tab} ({recruiters.filter(r => r.status === tab).length})
                    </button>
                ))}
            </div>

            {/* Recruiters Table */}
            <div className="bg-white rounded-xl shadow-md overflow-x-auto">
                <table className="w-full min-w-[900px]">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Business</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Tokens</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Submitted</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredRecruiters.map(recruiter => (
                            <tr key={recruiter.uid} className="border-t border-gray-200 hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm">
                                    {recruiter.firstName} {recruiter.lastName}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <div className="flex flex-col">
                                        <span className="font-medium">{recruiter.companyName || recruiter.businessType}</span>
                                        <span className="text-xs text-gray-500">{getBusinessTypeLabel(recruiter.businessType)}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-sm">{recruiter.email}</td>
                                <td className="px-4 py-3 text-sm">
                                    <span className="font-bold text-gradpath-green">{recruiter.tokens || 0}</span>
                                </td>
                                <td className="px-4 py-3 text-sm">{formatDate(recruiter.createdAt)}</td>
                                <td className="px-4 py-3 text-sm">
                                    {activeTab === 'pending' && (
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleStatusUpdate(recruiter.uid, 'approved')}
                                                disabled={loading[recruiter.uid]}
                                                className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs transition disabled:opacity-50"
                                            >
                                                {loading[recruiter.uid] ? <i className="fas fa-spinner fa-spin"></i> : 'Approve'}
                                            </button>
                                            <button
                                                onClick={() => handleStatusUpdate(recruiter.uid, 'rejected')}
                                                disabled={loading[recruiter.uid]}
                                                className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs transition disabled:opacity-50"
                                            >
                                                {loading[recruiter.uid] ? <i className="fas fa-spinner fa-spin"></i> : 'Reject'}
                                            </button>
                                        </div>
                                    )}
                                    {activeTab === 'approved' && (
                                        <div className="flex gap-2 flex-wrap">
                                            <button
                                                onClick={() => handleOpenTokenModal(recruiter)}
                                                className="px-3 py-1 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-xs transition"
                                            >
                                                <i className="fas fa-coins mr-1"></i> Add Tokens
                                            </button>
                                            <button
                                                onClick={() => handleStatusUpdate(recruiter.uid, 'rejected')}
                                                className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs transition"
                                            >
                                                Revoke
                                            </button>
                                        </div>
                                    )}
                                    {activeTab === 'rejected' && (
                                        <button
                                            onClick={() => handleStatusUpdate(recruiter.uid, 'pending')}
                                            className="px-3 py-1 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-xs transition"
                                        >
                                            Reconsider
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredRecruiters.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        No {activeTab} recruiters found
                    </div>
                )}
            </div>

            {/* Token Assignment Modal */}
            {tokenModal.isOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6 relative">
                        <button
                            onClick={handleCloseTokenModal}
                            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 text-2xl"
                        >
                            <i className="fas fa-times"></i>
                        </button>

                        <div className="text-center mb-4">
                            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <i className="fas fa-coins text-yellow-600 text-2xl"></i>
                            </div>
                            <h3 className="text-xl font-bold text-gradpath-dark">Add Tokens</h3>
                            <p className="text-gray-500 text-sm">
                                {tokenModal.recruiterName} • {tokenModal.companyName}
                            </p>
                            <p className="text-sm text-gray-400 mt-1">
                                Current balance: <span className="font-bold text-gradpath-green">{tokenModal.currentTokens}</span> tokens
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Number of Tokens
                                </label>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {presetAmounts.map(amount => (
                                        <button
                                            key={amount}
                                            onClick={() => setTokenAmount(amount)}
                                            className={`px-3 py-1 rounded-lg text-sm transition ${
                                                tokenAmount === amount
                                                    ? 'bg-gradpath-green text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                        >
                                            {amount}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">R</span>
                                    <input
                                        type="number"
                                        min="1"
                                        value={tokenAmount}
                                        onChange={(e) => setTokenAmount(Number(e.target.value))}
                                        className="flex-1 p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                                    />
                                    <span className="text-gray-500">tokens</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">
                                    Cost: R{tokenAmount * 50} (R50 per token)
                                </p>
                            </div>

                            {tokenError && (
                                <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                                    {tokenError}
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={handleCloseTokenModal}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAssignTokens}
                                    disabled={tokenLoading}
                                    className="flex-1 bg-gradpath-green text-white px-4 py-2 rounded-lg hover:bg-gradpath-dark transition disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {tokenLoading ? (
                                        <i className="fas fa-spinner fa-spin"></i>
                                    ) : (
                                        <i className="fas fa-plus"></i>
                                    )}
                                    Add Tokens
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecruiterManagement;